import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import {
  useChairmanEntryApplicationsQuery,
  useFinalizeEntryApplicationMutation,
} from "../api/committeeEntryReviewApi";
import { EntryApplicationDetails } from "./MyEntryReviewsPage";
import LoadError from "../components/LoadError";

const DECISION_STYLE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_STYLE: Record<string, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

function Card({ row }: { row: any }) {
  const app = row.application || {};
  const [decision, setDecision] = useState<"approved" | "rejected" | "">("");
  const [remark, setRemark] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [finalize, { isLoading }] = useFinalizeEntryApplicationMutation();

  // Already decided — the chairman has nothing left to do on it.
  const settled = ["approved", "rejected"].includes(String(app.status));

  const submit = async () => {
    setBanner(null);
    if (!decision) {
      setBanner({ ok: false, text: "Select approve or reject." });
      return;
    }
    if (
      !window.confirm(
        decision === "approved"
          ? "Approve this application? This opens the candidate's stage 1 logbook."
          : "Reject this application?",
      )
    ) {
      return;
    }
    try {
      const res: any = await finalize({
        id: app.id,
        decision,
        remark: remark.trim() || undefined,
      }).unwrap();
      setBanner({ ok: true, text: res?.message ?? "Application finalised." });
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "Could not finalise this application." });
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b bg-indigo-50/40 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900">
            {[app.surname, app.other_names].filter(Boolean).join(" ") || app.user?.name || "Applicant"}
          </div>
          <div className="text-sm text-gray-500">{app.category?.name ?? "Entry application"}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {[row.diet?.title, app.email ?? app.user?.email].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[11px] text-gray-500">Reviewer:</span>
            <span className="text-[11px] font-bold text-gray-700">{row.reviewer ?? "—"}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[11px] text-gray-500">Recommends:</span>
            <span
              className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                DECISION_STYLE[row.decision] ?? DECISION_STYLE.pending
              }`}
            >
              {row.decision}
            </span>
          </div>
          {settled ? (
            <span
              className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                STATUS_STYLE[String(app.status)] ?? ""
              }`}
            >
              finalised: {app.status}
            </span>
          ) : null}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {row.remark ? (
          <div className="text-sm text-gray-700 bg-gray-50 border rounded-lg p-3">
            <span className="font-semibold">Reviewer's remark: </span>
            {row.remark}
          </div>
        ) : null}

        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          <FileText className="w-4 h-4" />
          {open ? "Hide application" : "View application"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {open ? <EntryApplicationDetails app={app} /> : null}

        {banner ? (
          <div
            className={`text-sm rounded-lg p-3 border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                : "bg-red-50 border-red-100 text-red-700"
            }`}
          >
            {banner.text}
          </div>
        ) : null}

        {!settled ? (
          <>
            <div className="flex gap-2 pt-2 border-t">
              <button
                onClick={() => setDecision("approved")}
                className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  decision === "approved"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                }`}
              >
                Approve
              </button>
              <button
                onClick={() => setDecision("rejected")}
                className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  decision === "rejected"
                    ? "bg-red-600 text-white border-red-600"
                    : "border-red-200 text-red-600 hover:bg-red-50"
                }`}
              >
                Reject
              </button>
            </div>

            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              placeholder="Your remark / reason (shown to the applicant on rejection)"
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />

            <div className="flex justify-end">
              <button
                onClick={submit}
                disabled={isLoading || !decision}
                className="px-5 py-2.5 rounded-lg bg-gradient-purple text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? "Saving…" : "Finalise"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function ChairmanEntryApplicationsPage() {
  const { data: rows = [], isLoading, isError, error, refetch } = useChairmanEntryApplicationsQuery();
  const [showSettled, setShowSettled] = useState(false);

  const visible = useMemo(
    () =>
      showSettled
        ? rows
        : rows.filter((r: any) => !["approved", "rejected"].includes(String(r.application?.status))),
    [rows, showSettled],
  );

  const awaiting = rows.filter((r: any) => r.decision === "pending").length;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold">Entry Applications — Committee</h1>
        <p className="text-sm text-white/80 mt-1">
          Every entry application with a committee reviewer. Read the recommendation, then finalise.
          Approving opens the candidate's stage 1 logbook.
        </p>
        {rows.length ? (
          <p className="text-sm text-white/90 font-semibold mt-2">
            {rows.length} assigned · {awaiting} still awaiting a reviewer's recommendation
          </p>
        ) : null}
      </div>

      <label className="flex items-center gap-2 text-sm font-semibold text-gray-600">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={showSettled}
          onChange={(e) => setShowSettled(e.target.checked)}
        />
        Include applications already finalised
      </label>

      {isLoading ? (
        <div className="text-gray-600">Loading…</div>
      ) : isError ? (
        <LoadError error={error} onRetry={refetch} />
      ) : !visible.length ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
          Nothing is waiting on you right now.
        </div>
      ) : (
        visible.map((r: any) => <Card key={r.application?.id} row={r} />)
      )}
    </div>
  );
}
